// StudentOauthLink.container: OAuth→email link confirm; after confirm navigates to /login/oauth-wannabe#jwt like OAuth login.
// Mistake: sent users to signup profile wizard after link; they should use LoginOauthWannabe OAuth login flow instead.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

import * as AuthApi from "api/common/Auth.api";

import StudentOauthLinkComponent from "./StudentOauthLink.component";

const oauthProviderToSocialAccountType = (provider: string): string => {
    const map: Record<string, string> = {
        google: "socialAccountType:google",
        kakao: "socialAccountType:kakao",
        line: "socialAccountType:line",
        apple: "socialAccountType:apple",
    };
    return map[provider.toLowerCase()] || `socialAccountType:${provider}`;
};

const buildAvatarUrlFromSubUrl = (subUrl: string): string => {
    const path = (subUrl || "").trim().replace(/^\/+/, "").replace(/ /g, "%20");
    if (!path) {
        return "";
    }
    const prefix = (process.env.REACT_APP_PUBLIC_RESOURCE_URL_PREFIX || "").replace(/\/+$/, "");
    if (!prefix) {
        return path;
    }
    return `${prefix}/${path}?w=192&h=192`;
};

const resolveAvatarSrc = (pictureSub: string, pictureLegacy: string): string => {
    const fromSub = buildAvatarUrlFromSubUrl(pictureSub);
    if (fromSub) {
        return fromSub;
    }
    const leg = (pictureLegacy || "").trim();
    if (!leg) {
        return "";
    }
    if (leg.startsWith("http://") || leg.startsWith("https://")) {
        const sep = leg.includes("?") ? "&" : "?";
        return `${leg.replace(/ /g, "%20")}${sep}w=192&h=192`;
    }
    return buildAvatarUrlFromSubUrl(leg);
};

const StudentOauthLinkContainer = (props: { cancelSignup: () => void }) => {
    const { cancelSignup } = props;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isOnNetworking, setIsOnNetworking] = useState(false);
    const [isSendingVerificationEmail, setIsSendingVerificationEmail] = useState(false);
    const [fetchedDisplayName, setFetchedDisplayName] = useState("");
    const [fetchedPictureSub, setFetchedPictureSub] = useState("");
    const [hasVerifiedEmail, setHasVerifiedEmail] = useState(true);

    const resolveHasVerifiedEmail = (raw: unknown, fallback = true): boolean => {
        if (raw === true || raw === 1 || raw === "1") {
            return true;
        }
        if (raw === false || raw === 0 || raw === "0") {
            return false;
        }
        return fallback;
    };

    const oauthEmail = (searchParams.get("oauthEmail") || "").trim();
    const oauthProvider = (searchParams.get("oauthProvider") || "").trim();
    const oauthSocialId = (searchParams.get("oauthSocialId") || "").trim();
    const oauthSignupUuid = searchParams.get("oauthSignupUuid") || undefined;
    const accountName = (searchParams.get("accountName") || "").trim();
    const accountPictureSub = (searchParams.get("accountPictureSub") || "").trim();
    const accountPictureLegacy = (searchParams.get("accountPicture") || "").trim();

    useEffect(() => {
        if (!oauthEmail) {
            return;
        }
        let cancelled = false;
        (async () => {
            const res = await AuthApi.getOauthLinkAccountDisplay(oauthEmail);
            if (cancelled || !res || res.status !== 200 || !res.data?.data) {
                return;
            }
            const d = res.data.data as { displayName?: string; profileImageSubUrl?: string | null; hasVerifiedEmail?: number | boolean };
            const dn = typeof d.displayName === "string" ? d.displayName.trim() : "";
            const ps = typeof d.profileImageSubUrl === "string" ? d.profileImageSubUrl.trim() : "";
            const ve = resolveHasVerifiedEmail(d.hasVerifiedEmail, true);
            if (dn) {
                setFetchedDisplayName(dn);
            }
            if (ps) {
                setFetchedPictureSub(ps);
            }
            setHasVerifiedEmail(ve);
        })();
        return () => {
            cancelled = true;
        };
    }, [oauthEmail]);

    const displayName = (fetchedDisplayName || accountName || oauthEmail).trim() || oauthEmail;
    const pictureSubForAvatar = (fetchedPictureSub || accountPictureSub).trim();

    const avatarSrc = useMemo(
        () => resolveAvatarSrc(pictureSubForAvatar, accountPictureLegacy),
        [pictureSubForAvatar, accountPictureLegacy]
    );

    const extractJwt = (accountResult: any) =>
        accountResult?.data?.data?.jwt ??
        accountResult?.data?.data?.jwtToken ??
        accountResult?.data?.jwt ??
        accountResult?.data?.jwtToken ??
        null;

    const extractRefresh = (accountResult: any) =>
        accountResult?.data?.data?.refreshToken ?? accountResult?.data?.data?.refresh ?? accountResult?.data?.refreshToken ?? null;

    const onConfirmLink = async () => {
        if (!oauthEmail || !oauthProvider || !oauthSocialId) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errEmail"));
            return;
        }
        setIsOnNetworking(true);
        try {
            const displayRes = await AuthApi.getOauthLinkAccountDisplay(oauthEmail);
            const latestHasVerifiedEmail = resolveHasVerifiedEmail(displayRes?.data?.data?.hasVerifiedEmail, hasVerifiedEmail);
            setHasVerifiedEmail(latestHasVerifiedEmail);
            if (!latestHasVerifiedEmail) {
                alert(getLanguageValue(PAGE_TYPE.COMMON, "studentOauthLink.txtNeedEmailVerification"));
                return;
            }
            const accountResult = await AuthApi.confirmSocialLinkToEmail({
                uuid: oauthSignupUuid,
                socialAccountType: oauthProviderToSocialAccountType(oauthProvider),
                username: oauthEmail,
                name: displayName || oauthEmail,
                accountTypeCode: "accountType:student",
                socialId: oauthSocialId,
                termsOfService: true,
                personalPolicy: true,
                receiveEmail: true,
                receiveMessage: true,
            });
            const jwt = extractJwt(accountResult);
            const refresh = extractRefresh(accountResult);
            if (!jwt || !refresh) {
                console.error("StudentOauthLinkContainer: missing jwt or refresh after link confirm");
                return;
            }
            const schoolIdRaw = searchParams.get("school-id");
            const lead = new URLSearchParams();
            if (schoolIdRaw && /^\d+$/.test(schoolIdRaw)) {
                lead.set("school-id", schoolIdRaw);
            }
            const pathQs = lead.toString();
            const loginPath = pathQs ? `/login/oauth-wannabe?${pathQs}` : "/login/oauth-wannabe";
            const frag = new URLSearchParams();
            frag.set("jwt", jwt);
            frag.set("refresh", String(refresh));
            navigate(`${loginPath}#${frag.toString()}`, { replace: true });
        } catch (e) {
            console.error("StudentOauthLinkContainer:confirmSocialLinkToEmail", e);
        } finally {
            setIsOnNetworking(false);
        }
    };

    const onSendVerificationEmail = async () => {
        if (!oauthEmail) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupError.errEmail"));
            return;
        }
        setIsSendingVerificationEmail(true);
        try {
            const result = await AuthApi.sendOauthLinkVerificationEmail(oauthEmail);
            if (result?.status === 200) {
                alert(getLanguageValue(PAGE_TYPE.COMMON, "studentOauthLink.txtSentVerificationEmail"));
            }
        } catch (e) {
            console.error("StudentOauthLinkContainer:onSendVerificationEmail", e);
        } finally {
            setIsSendingVerificationEmail(false);
        }
    };

    return (
        <StudentOauthLinkComponent
            oauthEmail={oauthEmail}
            oauthProvider={oauthProvider}
            avatarSrc={avatarSrc}
            displayName={displayName}
            isOnNetworking={isOnNetworking}
            needsEmailVerification={!hasVerifiedEmail}
            isSendingVerificationEmail={isSendingVerificationEmail}
            onSendVerificationEmail={() => {
                void onSendVerificationEmail();
            }}
            onConfirmLink={() => {
                void onConfirmLink();
            }}
            onCancel={() => cancelSignup()}
        />
    );
};

export default StudentOauthLinkContainer;
